import React, { useEffect, useState } from "react";
import { api, formatBRL, formatDateBR, formatDateTimeBR, API } from "../lib/api";
import { toast } from "sonner";
import {
  FileSignature, RefreshCcw, DollarSign, Plus, Loader2, History,
  CheckCircle2, Calendar, Percent, Download, Ban, Clock, ArrowRightLeft, ExternalLink, Undo2, UserCog, Briefcase, AlertTriangle, RotateCcw, Coins, Settings2, ChevronDown, TrendingDown,
} from "lucide-react";
import { Link } from "react-router-dom";
import ConvertContractModal from "./ConvertContractModal";
import ReversalModal from "./ReversalModal";
import TransferSocioModal from "./TransferSocioModal";
import ChangeInterestModal from "./ChangeInterestModal";
import ChangeDueDayModal from "./ChangeDueDayModal";
import RecoveryToggleModal from "./RecoveryToggleModal";
import RenewWithAmortizationModal from "./RenewWithAmortizationModal";
import AdvanceSocioModal from "./AdvanceSocioModal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { useAuth } from "../lib/auth";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="fc-label">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-[#737373] mt-1">{hint}</div>}
    </div>
  );
}

export default function ContractSection({ clientId, canEdit, onContractChange, isIndicador, contractId = null, seq = null, initialContract = null, hideCreate = false }) {
  const [contract, setContract] = useState(initialContract);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Create form
  const [createOpen, setCreateOpen] = useState(false);
  const [cap, setCap] = useState("");
  const [pct, setPct] = useState("");
  const [vencDay, setVencDay] = useState("");
  const [firstDue, setFirstDue] = useState("");
  const [socioId, setSocioId] = useState("");
  const [socios, setSocios] = useState([]);
  const [capitalSource, setCapitalSource] = useState("socio"); // "socio" | "empresa"

  // Payment form
  const [payAmount, setPayAmount] = useState("");
  const [payNotes, setPayNotes] = useState("");
  // Renew form
  const [renewPct, setRenewPct] = useState("");
  const [renewAmount, setRenewAmount] = useState("");
  const [renewAmortOpen, setRenewAmortOpen] = useState(false); // iter70
  // PDF interest override
  const [pdfPct, setPdfPct] = useState("");
  // Convert modal
  const [convertOpen, setConvertOpen] = useState(false);
  // Reversal modal
  const [reversalOpen, setReversalOpen] = useState(false);
  // Transfer sócio modal
  const [transferOpen, setTransferOpen] = useState(false);
  // Change interest modal
  const [changeInterestOpen, setChangeInterestOpen] = useState(false);
  // Change due day / vencimento modal (Sprint 1 - Central de Alterações)
  const [changeDueDayOpen, setChangeDueDayOpen] = useState(false);
  const [recoveryModalMode, setRecoveryModalMode] = useState(null); // null | "enter" | "return"
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [advances, setAdvances] = useState([]);
  // Recovery status (>30d em atraso) — exibe badge RECUPERAÇÃO
  const [recovery, setRecovery] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAdminOrGestora = user?.role === "admin" || user?.role === "gestora";

  const load = async () => {
    setLoading(true);
    try {
      let data;
      if (contractId) {
        // Multi-contract: fetch this SPECIFIC contract
        const res = await api.get(`/contracts/${contractId}`);
        data = res.data;
      } else {
        // Legacy: fetch the latest contract for the client
        const res = await api.get(`/clients/${clientId}/contract`);
        data = res.data;
      }
      setContract(data);
      if (data && !isIndicador) {
        const { data: h } = await api.get(`/contracts/${data.id}/history`);
        setHistory(h);
      }
      // Check recovery status (badge RECUPERAÇÃO)
      if (data?.id && data.status === "ativo") {
        try {
          const { data: rec } = await api.get(`/recovery/check/contract/${data.id}`);
          setRecovery(rec);
        } catch (_) {
          setRecovery(null);
        }
      } else {
        setRecovery(null);
      }
      // Carrega advances do sócio (iter52) — admin/gestora vêem, sócio filtra pelo backend
      if (data?.id && !isIndicador) {
        try {
          const { data: advs } = await api.get(`/contracts/${data.id}/advances`);
          setAdvances(Array.isArray(advs) ? advs : []);
        } catch (_) {
          setAdvances([]);
        }
      }
    } catch (err) {
      setContract(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, contractId]);

  useEffect(() => {
    if (!isIndicador) {
      api.get("/socios").then(r => setSocios(r.data.filter(s => s.status === "ativo"))).catch(() => {});
    }
  }, [isIndicador]);

  const create = async (e) => {
    e.preventDefault();
    const isEmpresa = capitalSource === "empresa";
    if (!isEmpresa && !socioId) {
      toast.error("Selecione o sócio responsável pelo capital");
      return;
    }
    setBusy("create");
    try {
      const body = {
        capital: Number(cap),
        interest_percent: Number(pct),
        capital_source: capitalSource,
      };
      if (!isEmpresa) body.socio_id = socioId;
      if (vencDay) body.vencimento_day = Number(vencDay);
      if (firstDue) body.first_due_date = firstDue;
      await api.post(`/clients/${clientId}/contracts`, body);
      toast.success(isEmpresa ? "Contrato criado (Capital Próprio)" : "Contrato criado");
      setCreateOpen(false);
      setCap(""); setPct(""); setVencDay(""); setFirstDue(""); setSocioId(""); setCapitalSource("socio");
      load();
      onContractChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao criar contrato");
    } finally { setBusy(null); }
  };

  const pay = async (e) => {
    e.preventDefault();
    if (!contract) return;
    setBusy("pay");
    try {
      await api.post(`/contracts/${contract.id}/payments`, { amount: Number(payAmount), notes: payNotes });
      toast.success("Pagamento registrado");
      setPayAmount(""); setPayNotes("");
      load();
      onContractChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao pagar");
    } finally { setBusy(null); }
  };

  const quittance = async () => {
    if (!contract) return;
    const cc = contract.current_cycle;
    const totalDue = Number(cc?.total_due || 0);
    const totalPaid = Number(cc?.total_paid || 0);
    const balance = Math.max(totalDue - totalPaid, 0);
    const msg = balance > 0.005
      ? `Confirmar quitação total deste contrato?\n\nSaldo restante: ${formatBRL(balance)}\n\nO contrato será fechado, o capital retornará ao saldo operacional do sócio e o lucro será computado.`
      : `Este contrato já foi pago integralmente, mas está marcado como ATIVO. Deseja fechar o status agora?\n\nNenhum novo pagamento será criado.`;
    if (!window.confirm(msg)) return;
    setBusy("quittance");
    try {
      await api.post(`/contracts/${contract.id}/quittance`);
      toast.success(balance > 0.005 ? "Contrato quitado" : "Status do contrato alinhado");
      load();
      onContractChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao quitar contrato");
    } finally { setBusy(null); }
  };

  const renew = async () => {
    if (!contract) return;
    if (!window.confirm("Renovar este contrato? Será gerado um novo ciclo com o mesmo capital.")) return;
    setBusy("renew");
    try {
      const body = {};
      if (renewPct) body.new_interest_percent = Number(renewPct);
      if (renewAmount) body.paid_amount = Number(renewAmount);
      await api.post(`/contracts/${contract.id}/renew`, body);
      toast.success("Contrato renovado");
      setRenewPct(""); setRenewAmount("");
      load();
      onContractChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao renovar");
    } finally { setBusy(null); }
  };

  const cancelCt = async () => {
    if (!contract) return;
    if (!window.confirm("Cancelar este contrato? Esta ação não pode ser desfeita.")) return;
    setBusy("cancel");
    try {
      await api.post(`/contracts/${contract.id}/cancel`);
      toast.success("Contrato cancelado");
      load();
      onContractChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao cancelar");
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <section className="fc-card p-6">
        <div className="h-24 shimmer rounded"/>
      </section>
    );
  }

  // Indicator: simplified view
  if (isIndicador) {
    if (!contract) return null;
    const cc = contract.current_cycle;
    return (
      <section className="fc-card p-6 sm:p-7" data-testid="contract-indicator-view">
        <h2 className="font-display text-[17px] font-semibold tracking-tight mb-4 flex items-center gap-2">
          <FileSignature size={16} className="text-[#D4AF37]"/>
          {seq ? `Contrato #${String(seq).padStart(3, "0")}` : "Contrato"}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><div className="text-[10px] uppercase tracking-wider text-[#737373]">Status</div><div className="text-sm mt-1 capitalize">{contract.status}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#737373]">Capital</div><div className="font-mono-tab mt-1">{formatBRL(contract.capital)}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#737373]">Juros</div><div className="font-mono-tab mt-1">{contract.interest_percent}%</div></div>
          {cc && <div><div className="text-[10px] uppercase tracking-wider text-[#737373]">Vencimento</div><div className="text-sm mt-1">{formatDateBR(cc.due_date)}</div></div>}
        </div>
      </section>
    );
  }

  if (!contract) {
    // Se hideCreate=true (renderizado dentro de ContractsListSection), não mostra formulário
    // — a criação é gerenciada pelo wrapper.
    if (hideCreate) return null;
    return (
      <section className="fc-card p-6 sm:p-7" data-testid="contract-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-[17px] font-semibold tracking-tight flex items-center gap-2">
            <FileSignature size={16} className="text-[#D4AF37]"/> Contrato
          </h2>
          {canEdit && !createOpen && (
            <button onClick={()=>setCreateOpen(true)} className="fc-btn-gold flex items-center gap-2 text-sm" data-testid="btn-open-create-contract">
              <Plus size={14}/> Criar contrato
            </button>
          )}
        </div>
        {!createOpen && (
          <div className="text-[#737373] text-sm">Nenhum contrato ativo. Clique em "Criar contrato" para iniciar a operação.</div>
        )}
        {createOpen && (
          <form onSubmit={create} className="grid sm:grid-cols-2 gap-4 mt-2" data-testid="contract-create-form">
            <div className="sm:col-span-2">
              <label className="fc-label">Origem do capital</label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setCapitalSource("socio")}
                  className={`flex-1 px-3 py-2 rounded border text-sm transition ${
                    capitalSource === "socio"
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E8C547]"
                      : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                  }`}
                  data-testid="contract-capital-source-socio"
                >
                  Sócio externo (split 50/50)
                </button>
                <button
                  type="button"
                  onClick={() => setCapitalSource("empresa")}
                  className={`flex-1 px-3 py-2 rounded border text-sm transition ${
                    capitalSource === "empresa"
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                      : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                  }`}
                  data-testid="contract-capital-source-empresa"
                >
                  Capital Próprio (100% empresa)
                </button>
              </div>
              <div className="text-[11px] text-[#737373] mt-1.5">
                {capitalSource === "empresa"
                  ? "Contrato sem sócio — todo o lucro e capital recuperado vai para a empresa."
                  : "Sócio aporta o capital — lucro é dividido 50% sócio / 50% empresa."}
              </div>
            </div>
            <Field label="Capital emprestado (R$)">
              <input required type="number" step="0.01" min="0.01" className="fc-input" value={cap} onChange={e=>setCap(e.target.value)} data-testid="contract-capital"/>
            </Field>
            <Field label="Percentual de juros (% ao mês)">
              <input required type="number" step="0.01" min="0" className="fc-input" value={pct} onChange={e=>setPct(e.target.value)} data-testid="contract-interest"/>
            </Field>
            {capitalSource === "socio" && (
              <Field label="Sócio responsável pelo capital" hint="O sócio que aportará este contrato. Obrigatório.">
                <select required className="fc-input" value={socioId} onChange={e=>setSocioId(e.target.value)} data-testid="contract-socio">
                  <option value="">— Selecione —</option>
                  {socios.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.capital_disponivel !== undefined
                        ? `${s.name} (disponível: R$ ${(s.capital_disponivel ?? 0).toLocaleString("pt-BR", {minimumFractionDigits: 2})})`
                        : s.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Dia de vencimento (1-31)" hint="Mantido em todos os meses (cap. ao último dia disponível)">
              <input type="number" min="1" max="31" className="fc-input" value={vencDay} onChange={e=>setVencDay(e.target.value)} placeholder="Ex: 15" data-testid="contract-venc-day"/>
            </Field>
            <Field label="Primeiro vencimento (opcional)" hint="Se vazio, calcula +1 mês a partir de hoje no dia escolhido">
              <input type="date" className="fc-input" value={firstDue} onChange={e=>setFirstDue(e.target.value)} data-testid="contract-first-due"/>
            </Field>
            <div className="sm:col-span-2 flex gap-2 justify-end">
              <button type="button" onClick={()=>setCreateOpen(false)} className="fc-btn-ghost">Cancelar</button>
              <button type="submit" disabled={busy==="create"} className="fc-btn-gold flex items-center gap-2" data-testid="contract-create-submit">
                {busy==="create" && <Loader2 size={14} className="animate-spin"/>} Criar
              </button>
            </div>
          </form>
        )}
      </section>
    );
  }

  const cc = contract.current_cycle;
  const isActive = contract.status === "ativo";
  const isConverted = contract.status === "convertido";

  return (
    <section className="fc-card p-6 sm:p-7" data-testid="contract-section">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-[17px] font-semibold tracking-tight flex items-center gap-2">
            <FileSignature size={16} className="text-[#D4AF37]"/>
            {seq ? (
              <>
                <span data-testid={`contract-header-seq-${seq}`}>Contrato #{String(seq).padStart(3, "0")}</span>
              </>
            ) : (
              "Contrato"
            )}
            <span className={`badge-base ml-2 ${
              contract.status === "ativo" ? "badge-aprovado" :
              contract.status === "quitado" ? "badge-pago" :
              contract.status === "convertido" ? "bg-violet-500/15 text-violet-300 border-violet-500/40" :
              "bg-neutral-800 text-neutral-400"
            }`}>
              {contract.status === "ativo" ? "Ativo" :
               contract.status === "quitado" ? "Quitado" :
               contract.status === "convertido" ? `Convertido para ${contract.converted_to_kind || "parcelado/acordo"}` :
               "Cancelado"}
            </span>
            {recovery?.in_recuperacao && (
              <span
                className="badge-base ml-1 bg-amber-500/15 text-amber-200 border-amber-500/50 font-medium tracking-wider"
                title={`Em recuperação — ${recovery.dias_atraso} dias de atraso`}
                data-testid="contract-badge-recuperacao"
              >
                RECUPERAÇÃO · {recovery.dias_atraso}d
              </span>
            )}
            {(() => {
              // BUG FIX iter65: só mostra badge se a antecipação está ABERTA
              // e VINCULADA AO CICLO ATUAL. Antecipação de ciclo antigo não
              // deve vazar para o novo ciclo.
              const openAdvs = advances.filter(a =>
                a.status === "aberto" && cc?.id && a.cycle_id === cc.id
              );
              if (!openAdvs.length) return null;
              const totalOpen = openAdvs.reduce((s, a) => s + Number(a.remaining || 0), 0);
              return (
                <span
                  className="badge-base ml-1 bg-amber-600/15 text-amber-300 border-amber-500/50 font-medium tracking-wider"
                  title={`Forte antecipou R$ ${totalOpen.toFixed(2)} ao sócio — aguardando pagamento do cliente para compensar`}
                  data-testid="contract-badge-advance"
                >
                  SÓCIO JÁ ACERTADO · R$ {totalOpen.toFixed(2)}
                </span>
              );
            })()}
          </h2>
          <div className="text-[12px] text-[#737373] mt-1">
            Ciclo {cc?.cycle_number || "—"} • Vencimento dia {contract.vencimento_day} • {cc ? formatDateBR(cc.due_date) : "—"}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[12.5px]" data-testid="contract-socio-info">
            <span className="inline-flex items-center gap-1.5">
              <Briefcase size={12} className="text-[#D4AF37]"/>
              <span className="text-[10px] uppercase tracking-wider text-[#737373]">Sócio:</span>
              {contract.capital_source === "empresa" ? (
                <span className="text-violet-300 font-medium" data-testid="contract-socio-name">Capital Próprio (Empresa)</span>
              ) : contract.socio_name ? (
                <span className="text-white font-medium" data-testid="contract-socio-name">{contract.socio_name}</span>
              ) : (
                <span className="text-[#737373] italic" data-testid="contract-socio-name">não vinculado</span>
              )}
            </span>
          </div>
          {isConverted && contract.converted_to_installment_id && (
            <Link
              to={`/parcelados/${contract.converted_to_installment_id}`}
              className="inline-flex items-center gap-1 text-[12px] text-[#D4AF37] hover:text-[#E8C547] mt-1.5"
              data-testid="link-converted-installment"
            >
              <ExternalLink size={11}/> Ver {contract.converted_to_kind || "parcelado/acordo"}
            </Link>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`${API}/contracts/${contract.id}/pdf${pdfPct ? `?interest_percent=${pdfPct}` : ""}`} target="_blank" rel="noreferrer" className="fc-btn-ghost flex items-center gap-1.5 text-sm" data-testid="contract-pdf">
            <Download size={14}/> PDF
          </a>
          {isActive && canEdit && (
            <button
              onClick={() => setConvertOpen(true)}
              className="fc-btn-ghost flex items-center gap-1.5 text-sm text-violet-300 border-violet-500/30"
              data-testid="contract-convert"
            >
              <ArrowRightLeft size={14}/> Converter para Parcelado/Acordo
            </button>
          )}
          {isAdminOrGestora && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="fc-btn-ghost flex items-center gap-1.5 text-sm text-[#D4AF37] border-[#D4AF37]/30"
                  data-testid="contract-alteracoes-menu-trigger"
                  title="Central de Alterações Operacionais"
                >
                  <Settings2 size={14}/> Alterações Operacionais <ChevronDown size={12}/>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-64 bg-[#0a0a0a] border-[#262626] text-white"
                data-testid="contract-alteracoes-menu"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#737373]">
                  Central de Alterações
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#262626]"/>
                {isActive && (
                  <DropdownMenuItem
                    onSelect={() => setChangeDueDayOpen(true)}
                    className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#E8C547]"
                    data-testid="menu-item-change-due-day"
                  >
                    <Calendar size={14} className="text-[#D4AF37]"/>
                    <div className="flex flex-col">
                      <span className="text-[13px]">Alterar vencimento</span>
                      <span className="text-[10.5px] text-[#737373]">Dia mensal ou parcela específica</span>
                    </div>
                  </DropdownMenuItem>
                )}
                {isActive && (
                  <DropdownMenuItem
                    onSelect={() => setTransferOpen(true)}
                    className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#E8C547]"
                    data-testid="menu-item-transfer-socio"
                  >
                    <UserCog size={14} className="text-amber-300"/>
                    <div className="flex flex-col">
                      <span className="text-[13px]">
                        {contract.capital_source === "empresa" ? "Transferir destino" : "Trocar sócio"}
                      </span>
                      <span className="text-[10.5px] text-[#737373]">Entre sócios ou Capital Próprio</span>
                    </div>
                  </DropdownMenuItem>
                )}
                {isActive && (
                  <DropdownMenuItem
                    onSelect={() => setChangeInterestOpen(true)}
                    className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#E8C547]"
                    data-testid="menu-item-change-interest"
                  >
                    <Percent size={14} className="text-[#D4AF37]"/>
                    <div className="flex flex-col">
                      <span className="text-[13px]">Alterar % de juros</span>
                      <span className="text-[10.5px] text-[#737373]">Recalcula o ciclo atual</span>
                    </div>
                  </DropdownMenuItem>
                )}
                {isActive && contract.capital_source !== "empresa" && contract.socio_id && (
                  <DropdownMenuItem
                    onSelect={() => setAdvanceOpen(true)}
                    className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#E8C547]"
                    data-testid="menu-item-advance-socio"
                  >
                    <Coins size={14} className="text-amber-200"/>
                    <div className="flex flex-col">
                      <span className="text-[13px]">Antecipar acerto do sócio</span>
                      <span className="text-[10.5px] text-[#737373]">Forte adianta 50% do juros</span>
                    </div>
                  </DropdownMenuItem>
                )}
                {isActive && (
                  <DropdownMenuItem
                    onSelect={() => setRecoveryModalMode(recovery?.in_recuperacao ? "return" : "enter")}
                    className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#E8C547]"
                    data-testid={recovery?.in_recuperacao ? "menu-item-recovery-return" : "menu-item-recovery-enter"}
                  >
                    {recovery?.in_recuperacao
                      ? <RotateCcw size={14} className="text-emerald-300"/>
                      : <AlertTriangle size={14} className="text-amber-300"/>}
                    <div className="flex flex-col">
                      <span className="text-[13px]">
                        {recovery?.in_recuperacao ? "Retornar ao operacional" : "Mover para Recuperação"}
                      </span>
                      <span className="text-[10.5px] text-[#737373]">
                        {recovery?.in_recuperacao ? "Volta à carteira normal" : "Sai do dashboard operacional"}
                      </span>
                    </div>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-[#262626]"/>
                <DropdownMenuItem
                  onSelect={() => setReversalOpen(true)}
                  className="cursor-pointer focus:bg-red-500/10 focus:text-red-200"
                  data-testid="menu-item-reversal"
                >
                  <Undo2 size={14} className="text-red-300"/>
                  <div className="flex flex-col">
                    <span className="text-[13px]">Corrigir / Estornar</span>
                    <span className="text-[10.5px] text-[#737373]">Desfaz último lançamento</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isActive && (
            <button onClick={cancelCt} disabled={busy==="cancel"} className="fc-btn-ghost flex items-center gap-1.5 text-sm text-red-300 border-red-500/20" data-testid="contract-cancel">
              <Ban size={14}/> Cancelar
            </button>
          )}
          {!isActive && canEdit && !createOpen && (
            <button onClick={()=>setCreateOpen(true)} className="fc-btn-gold flex items-center gap-1.5 text-sm" data-testid="btn-new-contract">
              <Plus size={14}/> Novo contrato
            </button>
          )}
        </div>
      </div>

      {convertOpen && (
        <ConvertContractModal
          contract={contract}
          onClose={() => setConvertOpen(false)}
          onConverted={() => { setConvertOpen(false); load(); onContractChange && onContractChange(); }}
        />
      )}

      {reversalOpen && (
        <ReversalModal
          contract={contract}
          onClose={() => setReversalOpen(false)}
          onDone={() => { setReversalOpen(false); load(); onContractChange && onContractChange(); }}
        />
      )}

      {recoveryModalMode && (
        <RecoveryToggleModal
          mode={recoveryModalMode}
          contract={{ id: contract.id, recovery_id: recovery?.recovery_id }}
          onClose={() => setRecoveryModalMode(null)}
          onDone={() => {
            setRecoveryModalMode(null);
            load();
            onContractChange && onContractChange();
          }}
        />
      )}

      {renewAmortOpen && (
        <RenewWithAmortizationModal
          contract={contract}
          onClose={() => setRenewAmortOpen(false)}
          onDone={() => {
            setRenewAmortOpen(false);
            load();
            onContractChange && onContractChange();
          }}
        />
      )}

      {advanceOpen && (
        <AdvanceSocioModal
          contract={contract}
          onClose={() => setAdvanceOpen(false)}
          onDone={() => {
            setAdvanceOpen(false);
            load();
            onContractChange && onContractChange();
          }}
        />
      )}

      {transferOpen && (
        <TransferSocioModal
          contract={contract}
          onClose={() => setTransferOpen(false)}
          onDone={() => { setTransferOpen(false); load(); onContractChange && onContractChange(); }}
        />
      )}

      {changeInterestOpen && (
        <ChangeInterestModal
          contract={contract}
          onClose={() => setChangeInterestOpen(false)}
          onDone={() => { setChangeInterestOpen(false); load(); onContractChange && onContractChange(); }}
        />
      )}

      {changeDueDayOpen && (
        <ChangeDueDayModal
          contract={contract}
          onClose={() => setChangeDueDayOpen(false)}
          onDone={() => { setChangeDueDayOpen(false); load(); onContractChange && onContractChange(); }}
        />
      )}

      {/* Form de criar novo contrato (quando o atual não está ativo) */}
      {!isActive && createOpen && canEdit && (
        <form onSubmit={create} className="grid sm:grid-cols-2 gap-4 mt-2 mb-5 p-4 rounded-md border border-[#D4AF37]/20 bg-[#0f0f0f]" data-testid="contract-recreate-form">
          <div className="sm:col-span-2 text-[11px] uppercase tracking-wider text-[#D4AF37]">Novo contrato para este cliente</div>
          <Field label="Capital emprestado (R$)">
            <input required type="number" step="0.01" min="0.01" className="fc-input" value={cap} onChange={e=>setCap(e.target.value)} data-testid="recreate-capital"/>
          </Field>
          <Field label="Percentual de juros (% ao mês)">
            <input required type="number" step="0.01" min="0" className="fc-input" value={pct} onChange={e=>setPct(e.target.value)} data-testid="recreate-interest"/>
          </Field>
          <Field label="Sócio responsável pelo capital">
            <select required className="fc-input" value={socioId} onChange={e=>setSocioId(e.target.value)} data-testid="recreate-socio">
              <option value="">— Selecione —</option>
              {socios.map(s => (
                <option key={s.id} value={s.id}>
                  {s.capital_disponivel !== undefined
                    ? `${s.name} (disponível: R$ ${(s.capital_disponivel ?? 0).toLocaleString("pt-BR", {minimumFractionDigits: 2})})`
                    : s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Dia de vencimento (1-31)">
            <input type="number" min="1" max="31" className="fc-input" value={vencDay} onChange={e=>setVencDay(e.target.value)} placeholder="Ex: 15" data-testid="recreate-venc-day"/>
          </Field>
          <Field label="Primeiro vencimento (opcional)">
            <input type="date" className="fc-input" value={firstDue} onChange={e=>setFirstDue(e.target.value)} data-testid="recreate-first-due"/>
          </Field>
          <div className="sm:col-span-2 flex gap-2 justify-end">
            <button type="button" onClick={()=>setCreateOpen(false)} className="fc-btn-ghost">Cancelar</button>
            <button type="submit" disabled={busy==="create"} className="fc-btn-gold flex items-center gap-2" data-testid="recreate-submit">
              {busy==="create" && <Loader2 size={14} className="animate-spin"/>} Criar novo contrato
            </button>
          </div>
        </form>
      )}

      {/* Cycle KPIs */}
      {cc && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          <div className="p-3 rounded-md bg-[#0f0f0f] border border-[#262626]">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Capital</div>
            <div className="font-mono-tab text-base mt-1">{formatBRL(cc.capital)}</div>
          </div>
          <div className="p-3 rounded-md bg-[#0f0f0f] border border-[#262626]">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Juros ({cc.interest_percent}%)</div>
            <div className="font-mono-tab text-base mt-1 text-[#D4AF37]">{formatBRL(cc.interest_amount)}</div>
          </div>
          <div className="p-3 rounded-md bg-[#0f0f0f] border border-[#262626]">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Total devido</div>
            <div className="font-mono-tab text-base mt-1">{formatBRL(cc.total_due)}</div>
          </div>
          <div className="p-3 rounded-md bg-[#0f0f0f] border border-[#262626]">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Saldo restante</div>
            <div className={`font-mono-tab text-base mt-1 ${cc.balance > 0 ? "text-yellow-300" : "text-emerald-300"}`}>{formatBRL(cc.balance)}</div>
          </div>
          <div className="p-3 rounded-md bg-[#0f0f0f] border border-[#262626]">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Total pago no ciclo</div>
            <div className="font-mono-tab text-base mt-1 text-emerald-300">{formatBRL(cc.total_paid)}</div>
          </div>
          <div className="p-3 rounded-md bg-[#0f0f0f] border border-[#262626]">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Renovar com</div>
            <div className="font-mono-tab text-base mt-1 text-[#D4AF37]">{formatBRL(Math.max(cc.interest_amount - cc.total_paid, 0))}</div>
          </div>
          <div className="p-3 rounded-md bg-[#0f0f0f] border border-[#262626] col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Status do ciclo</div>
            <div className="text-sm mt-1 capitalize">{cc.status}</div>
          </div>
        </div>
      )}

      {/* Actions */}
      {isActive && canEdit && (
        <div className="grid lg:grid-cols-2 gap-4">
          <form onSubmit={pay} className="p-4 rounded-md border border-[#262626] bg-[#0f0f0f]" data-testid="payment-form">
            <h3 className="font-display text-sm font-semibold tracking-tight flex items-center gap-2 mb-3">
              <DollarSign size={14} className="text-emerald-300"/> Registrar pagamento parcial
            </h3>
            <div className="space-y-3">
              <input required type="number" step="0.01" min="0.01" className="fc-input" placeholder="Valor (R$)" value={payAmount} onChange={e=>setPayAmount(e.target.value)} data-testid="payment-amount"/>
              <input className="fc-input" placeholder="Observação (opcional)" value={payNotes} onChange={e=>setPayNotes(e.target.value)} data-testid="payment-notes"/>
              <button type="submit" disabled={busy==="pay" || busy==="quittance"} className="fc-btn-gold w-full flex items-center justify-center gap-2 text-sm" data-testid="payment-submit">
                {busy==="pay" ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>} Lançar pagamento
              </button>
              <button
                type="button"
                onClick={quittance}
                disabled={busy==="pay" || busy==="quittance"}
                className="w-full flex items-center justify-center gap-2 text-sm py-2.5 rounded-md border border-emerald-500/40 text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="quittance-button"
                title="Fecha o contrato com pagamento exato do saldo restante. Devolve o capital ao saldo operacional do sócio."
              >
                {busy==="quittance" ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>} Quitar contrato (saldo: {formatBRL(Math.max((cc?.total_due || 0) - (cc?.total_paid || 0), 0))})
              </button>
            </div>
            <div className="text-[11px] text-[#737373] mt-2">
              Use &quot;Quitar contrato&quot; para fechar o contrato em um clique — o sistema calcula automaticamente o saldo restante, devolve o capital ao sócio e computa o lucro. Para abater capital parcialmente, use &quot;Lançar pagamento&quot;.
            </div>
          </form>

          <div className="p-4 rounded-md border border-[#262626] bg-[#0f0f0f]" data-testid="renew-form">
            <h3 className="font-display text-sm font-semibold tracking-tight flex items-center gap-2 mb-3">
              <RefreshCcw size={14} className="text-[#D4AF37]"/> Renovar contrato
            </h3>
            <div className="space-y-3">
              <input type="number" step="0.01" className="fc-input" placeholder={`Valor de juros pagos (padrão: ${formatBRL(Math.max((cc?.interest_amount || 0) - (cc?.total_paid || 0), 0))})`} value={renewAmount} onChange={e=>setRenewAmount(e.target.value)} data-testid="renew-amount"/>
              <input type="number" step="0.01" className="fc-input" placeholder={`Novo % de juros (atual: ${contract.interest_percent}%)`} value={renewPct} onChange={e=>setRenewPct(e.target.value)} data-testid="renew-percent"/>
              <button type="button" onClick={renew} disabled={busy==="renew"} className="fc-btn-ghost w-full flex items-center justify-center gap-2 text-sm border-[#D4AF37]/30 text-[#D4AF37]" data-testid="renew-submit">
                {busy==="renew" ? <Loader2 size={14} className="animate-spin"/> : <RefreshCcw size={14}/>} Renovar ciclo
              </button>
              <button
                type="button"
                onClick={() => setRenewAmortOpen(true)}
                className="w-full flex items-center justify-center gap-2 text-[12.5px] py-2 rounded-md border border-emerald-500/30 text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors"
                data-testid="renew-amort-open"
                title="Renovar o ciclo E amortizar parte do capital em uma operação"
              >
                <TrendingDown size={14}/> Renovar + amortizar capital
              </button>
            </div>
            <div className="text-[11px] text-[#737373] mt-2">
              Renovar fecha o ciclo atual, cria novo ciclo com mesmo capital e avança o vencimento para o próximo mês (mesmo dia). Use &quot;+ amortizar&quot; quando o cliente pagar juros + parte do capital.
            </div>
          </div>
        </div>
      )}

      {/* PDF interest override */}
      {canEdit && (
        <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-2">
          <span className="text-[12px] text-[#737373]">Gerar PDF com % alternativo:</span>
          <input type="number" step="0.01" className="fc-input text-sm max-w-[140px]" placeholder={`${contract.interest_percent}`} value={pdfPct} onChange={e=>setPdfPct(e.target.value)} data-testid="pdf-interest-override"/>
          <span className="text-[11px] text-[#737373]">vazio = usa o juros atual do contrato</span>
        </div>
      )}

      {/* Payments + history table */}
      <div className="mt-6">
        <h3 className="font-display text-sm font-semibold tracking-tight flex items-center gap-2 mb-3">
          <History size={14} className="text-[#D4AF37]"/> Histórico financeiro
        </h3>
        {history.length === 0 ? (
          <div className="text-[#737373] text-sm">Sem eventos.</div>
        ) : (
          <ul className="space-y-2.5">
            {history.slice().reverse().map(h => (
              <li key={h.id} className="flex gap-3 text-[13px]" data-testid={`history-${h.event}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0"/>
                <div className="flex-1">
                  <div>
                    <span className="capitalize text-white">{h.event === "created" ? "Contrato criado"
                      : h.event === "payment" ? `Pagamento ${h.data?.type || ""}`
                      : h.event === "renewal" ? "Renovação"
                      : h.event === "quittance" ? "Quitação"
                      : h.event === "cancel" ? "Cancelamento"
                      : h.event === "socio_transferred" ? "Troca de sócio"
                      : h.event === "interest_percent_changed" ? "Alteração de % de juros"
                      : h.event === "due_day_changed" ? "Alteração de vencimento" : h.event}</span>
                    {h.data?.amount && <span className="text-[#D4AF37] ml-2 font-mono-tab">{formatBRL(h.data.amount)}</span>}
                    {h.data?.balance_after !== undefined && <span className="text-[#737373] ml-2">→ saldo {formatBRL(h.data.balance_after)}</span>}
                    {h.data?.new_due_date && h.event !== "due_day_changed" && <span className="text-[#737373] ml-2">novo venc. {formatDateBR(h.data.new_due_date)}</span>}
                    {h.event === "socio_transferred" && (
                      <span className="text-amber-300 ml-2">{h.data?.from_socio_name || "—"} → {h.data?.to_socio_name || "—"}</span>
                    )}
                    {h.event === "interest_percent_changed" && (
                      <span className="text-[#D4AF37] ml-2 font-mono-tab">{h.data?.old_interest_percent}% → {h.data?.new_interest_percent}%</span>
                    )}
                    {h.event === "due_day_changed" && (
                      <span className="text-[#D4AF37] ml-2 font-mono-tab">
                        {h.data?.mode === "monthly"
                          ? `dia ${h.data?.old_vencimento_day || "—"} → ${h.data?.new_vencimento_day}`
                          : `${h.data?.old_due_date ? formatDateBR(h.data.old_due_date) : "—"} → ${h.data?.new_due_date ? formatDateBR(h.data.new_due_date) : "—"}`}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#737373] mt-0.5">
                    {formatDateTimeBR(h.created_at)} · {h.user_email}
                    {h.data?.notes ? ` · ${h.data.notes}` : ""}
                    {h.event === "socio_transferred" && h.data?.reason ? ` · Motivo: ${h.data.reason}` : ""}
                    {h.event === "interest_percent_changed" && h.data?.reason ? ` · Motivo: ${h.data.reason}` : ""}
                    {h.event === "due_day_changed" && h.data?.reason ? ` · Motivo: ${h.data.reason}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
